import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useReactTable, getCoreRowModel, flexRender, } from '@tanstack/react-table';
import { CenteredRow, StyledBodyTr, StyledContainer, StyledTable, StyledTableContainer, StyledTbody, StyledTd, StyledTh, StyledThead, } from './table.styles';
import { ActionBar } from './components/ActionBar';
import { Pagination } from './components/Pagination';
import { NoResults } from './components/NoResults';
import { LoadingSpinner } from '../status-indicators/LoadingSpinner/LoadingSpinner';
import { isNil } from 'lodash';
export function Table({ columns: defaultColumns, data, className, isFixed = false, currentPage, totalPageCount, totalRowCount, paginationLimit = 1000, nextPage, previousPage, TableActions, error, enableRowSelection = true, clearFilters, hasFiltersApplied, showPagination = true, loading = false, defaultPinnedColumns, }) {
    var _a;
    const columns = useMemo(() => defaultColumns, []);
    const defaultData = useMemo(() => [], []);
    const [rowSelection, setRowSelection] = useState({});
    const [windowWidth, setWindowWidth] = useState(window.innerWidth);
    const [formattedColumns, setFormattedColumns] = useState(columns);
    const thRefs = useRef({});
    // Tracks changes to browser width to allow for updating of column widths
    useEffect(() => {
        const handleResize = () => {
            setWindowWidth(window.innerWidth);
        };
        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);
    // Sets formattedColumns with correct column widths
    useEffect(() => {
        const updatedColumns = [];
        formattedColumns.forEach((column) => {
            updatedColumns.push(Object.assign(Object.assign({}, column), { size: thRefs.current[column.id].offsetWidth }));
        });
        setFormattedColumns(updatedColumns);
    }, [data, columns, windowWidth]);
    const table = useReactTable({
        data: data !== null && data !== void 0 ? data : defaultData,
        columns: formattedColumns,
        state: {
            rowSelection,
        },
        enableRowSelection,
        onRowSelectionChange: setRowSelection,
        getCoreRowModel: getCoreRowModel(),
        manualPagination: true,
        debugTable: true,
        initialState: {
            columnPinning: {
                left: defaultPinnedColumns,
            },
        },
    });
    const totalRowsOnPage = table.getRowModel().rows.length;
    const hasPaginationProps = !isNil(currentPage) &&
        !isNil(totalPageCount) &&
        !isNil(totalRowsOnPage) &&
        !isNil(totalRowCount) &&
        nextPage &&
        previousPage;
    // Sets style attributes for pinned columns
    const getCommonPinningStyles = (column) => {
        const isPinned = column.getIsPinned();
        const isLastLeftPinnedColumn = isPinned === 'left' && column.getIsLastColumn('left');
        return {
            boxShadow: isLastLeftPinnedColumn ? '-4px 0 4px -4px gray inset' : undefined,
            left: isPinned === 'left' ? `${column.getStart('left')}px` : undefined,
            opacity: isPinned ? 0.95 : 1,
            position: isPinned ? 'sticky' : 'initial',
            zIndex: isPinned ? 1 : 0,
        };
    };
    return (React.createElement(StyledContainer, { "$isFixed": isFixed },
        React.createElement(StyledTableContainer, { "$isFixed": isFixed },
            React.createElement(StyledTable, { "$isFixed": isFixed, className: className, "$isLoading": loading },
                React.createElement(StyledThead, null, table.getHeaderGroups().map((headerGroup) => (React.createElement("tr", { key: headerGroup.id }, headerGroup.headers.map((header) => {
                    var _a, _b, _c, _d, _e;
                    const { column } = header;
                    return (React.createElement(StyledTh, { key: header.id, ref: (el) => {
                            if (el)
                                thRefs.current[column.id] = el;
                        }, colSpan: header.colSpan, "$noPadding": !!((_c = (_b = (_a = header.column.columnDef.meta) === null || _a === void 0 ? void 0 : _a.styles) === null || _b === void 0 ? void 0 : _b.header) === null || _c === void 0 ? void 0 : _c.noPadding), "$hasDefinedColumnSize": !!((_e = (_d = header.column.columnDef.meta) === null || _d === void 0 ? void 0 : _d.styles) === null || _e === void 0 ? void 0 : _e.definedColumnSize), "$width": header.column.getSize(), style: Object.assign({}, getCommonPinningStyles(column)) }, flexRender(header.column.columnDef.header, header.getContext())));
                }))))),
                React.createElement(StyledTbody, null, (error === null || error === void 0 ? void 0 : error.hasError) && !loading ? (React.createElement(StyledBodyTr, { "$isSelected": false },
                    React.createElement(CenteredRow, { colSpan: columns.length },
                        React.createElement(NoResults, { clearFilters: clearFilters, hasFiltersApplied: hasFiltersApplied, message: (_a = error.errorMessage) !== null && _a !== void 0 ? _a : 'There was an error loading the data.' })))) : data.length === 0 && !loading ? (React.createElement(StyledBodyTr, { "$isSelected": false },
                    React.createElement(CenteredRow, { colSpan: columns.length },
                        React.createElement(NoResults, { clearFilters: clearFilters, hasFiltersApplied: hasFiltersApplied, message: "No results found." })))) : (React.createElement(React.Fragment, null,
                    table.getRowModel().rows.map((row) => (React.createElement(StyledBodyTr, { key: row.id, "$isSelected": row.getIsSelected() }, row.getVisibleCells().map((cell) => {
                        var _a, _b, _c, _d, _e, _f, _g, _h;
                        const { columnDef } = cell.column;
                        return (React.createElement(StyledTd, { key: cell.id, "$noPadding": !!((_c = (_b = (_a = columnDef.meta) === null || _a === void 0 ? void 0 : _a.styles) === null || _b === void 0 ? void 0 : _b.cell) === null || _c === void 0 ? void 0 : _c.noPadding), "$textAlign": (_f = (_e = (_d = columnDef.meta) === null || _d === void 0 ? void 0 : _d.styles) === null || _e === void 0 ? void 0 : _e.cell) === null || _f === void 0 ? void 0 : _f.textAlign, "$hasDefinedColumnSize": !!((_h = (_g = columnDef.meta) === null || _g === void 0 ? void 0 : _g.styles) === null || _h === void 0 ? void 0 : _h.definedColumnSize), "$width": cell.column.getSize(), style: Object.assign({}, getCommonPinningStyles(cell.column)) }, flexRender(columnDef.cell, cell.getContext())));
                    })))),
                    loading && (React.createElement(StyledBodyTr, { "$isSelected": false },
                        React.createElement(CenteredRow, { colSpan: columns.length, "$isLoading": loading },
                            React.createElement(LoadingSpinner, { size: "36px" })))))))),
            Object.keys(rowSelection).length > 0 && (React.createElement(ActionBar, { table: table, isFixed: isFixed, TableActions: TableActions }))),
        showPagination && hasPaginationProps ? (React.createElement(Pagination, { currentPage: currentPage, totalPageCount: totalPageCount, totalRowCount: totalRowCount, paginationLimit: paginationLimit, totalRowsOnPage: totalRowsOnPage, nextPage: nextPage, previousPage: previousPage })) : null));
}
//# sourceMappingURL=Table.js.map